/**
 * MANAGEMENT (14) · Pricing + billing response shapes — the EXACT client views the cross-lab pricing
 * service serializes. Code-verified against server/src/modules/management/pricing.service.js
 * ({toPlanDto}, {toPaymentDto}) + pricing.validators.js (the wire body shapes).
 *
 * ADDITIVE to the shell ../types.ts: it reuses the shared `Page<T>` shape via a local re-declaration only
 * where the server returns a plain `{ items, total }` count envelope (the pricing list is NOT the paginated
 * `Page<T>` — the service returns `{ items, total }` with no page cursor). Money is ALWAYS integer centimes
 * DZD on the wire (never a float); the form edits the major DZD unit and converts at the boundary. No PHI
 * rides any of these seams (the commercial layer is PHI-free, L-4).
 */

/** The catalogue tier ladder (mirrors the server PLAN_TIERS + the PlanTier enum). Display order. */
export type PlanTier = "starter" | "standard" | "pro" | "enterprise";
export const PLAN_TIERS: ReadonlyArray<PlanTier> = ["starter", "standard", "pro", "enterprise"];

/** The billing cycles a checkout can charge (mirrors the server BILLING_CYCLES). */
export type BillingCycle = "mensuel" | "annuel";
export const BILLING_CYCLES: ReadonlyArray<BillingCycle> = ["mensuel", "annuel"];

/** The SubscriptionPayment lifecycle statuts (mirrors the server SubscriptionPayment.statut). */
export type PaymentStatut = "en_attente" | "paye" | "echec";

/** One PlanCatalogue row — the operator/pricing-page DTO (BigInt-free; money integer centimes). */
export interface PlanRow {
  code: string;
  nom: string;
  description: string | null;
  prixMensuelCentimes: number;
  prixAnnuelCentimes: number | null;
  modules: string[];
  cloudSku: string[];
  tier: PlanTier;
  actif: boolean;
  ordre: number;
  version: number;
}

/** The plan list envelope — a plain count list (NOT the paginated `Page<T>`; the service returns this shape). */
export interface PlanList {
  items: PlanRow[];
  total: number;
}

/** One SubscriptionPayment row — the operator billing DTO (BigInt ids stringified; money integer centimes). */
export interface PaymentRow {
  id: string;
  laboratoireId: string;
  planCode: string;
  montantCentimes: number;
  cycle: BillingCycle;
  statut: PaymentStatut;
  chargilyCheckoutId: string | null;
  chargilyTransactionId: string | null;
  checkoutUrl: string | null;
  paidAt: string | null;
  createdAt: string | null;
}

/** The payments list envelope (a lab's history — a plain count list). */
export interface PaymentList {
  items: PaymentRow[];
  total: number;
}

/** The createCheckout return — a pending PaymentRow + the checkout URL + whether it was simulated. */
export interface CheckoutResult extends PaymentRow {
  simulated: boolean;
  mode: string;
}

// ── Write bodies (snake_case — mirror the Zod validator schemas exactly so the contract never drifts) ──

/** POST /management/pricing/plans body — publish a NEW plan tier. */
export interface CreatePlanBody {
  code: string;
  nom: string;
  description?: string | null;
  prix_mensuel_centimes: number;
  prix_annuel_centimes?: number | null;
  modules?: string[];
  cloud_sku?: string[];
  tier: PlanTier;
  ordre?: number;
}

/** PUT /management/pricing/plans/:code body — REISSUE (partial patch; at least one editable field). */
export interface UpdatePlanBody {
  nom?: string;
  description?: string | null;
  prix_mensuel_centimes?: number;
  prix_annuel_centimes?: number | null;
  modules?: string[];
  cloud_sku?: string[];
  ordre?: number;
}

/** POST /management/pricing/labs/:laboratoireId/assign body — assign a catalogued plan to a lab. */
export interface AssignPlanBody {
  plan_code: string;
  statut?: string;
  reason?: string | null;
  source_ref?: string | null;
}

/** POST /management/billing/labs/:laboratoireId/checkout body — create a Chargily (or simulated) checkout. */
export interface CreateCheckoutBody {
  plan_code: string;
  cycle: BillingCycle;
}
